import { useState, useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'

import { ApiErrorCode } from '@/api/apiTypes'
import { BaseView, BaseText } from '@/components/base'
import { PhoneNumberInput } from '@/components/PhoneNumberInput'
import { usePhoneValidation } from '@/hooks'
import { MessagingMethodsBottomSheet } from '@/components/MessagingMethodsBottomSheet'
import { useSendRecoveryCodeToPhone } from '@/domain'
import { IPhoneDataSchema, usePhoneDataSchema } from '@/schemas'
import { useAuthScopedStore } from '@/store/scoped'
import { useAuthPreferencesStore } from '@/store'
import { useGetCountriesCatalog } from '@/domain/countries/useCases'
import type {
  MessagingMethodCode,
  MessagingMethodsDTO,
} from '@/domain/messagingMethods/messagingMethodsTypes'
import type { PhoneData } from '@/types'
import { responsiveValueAdapter } from '@/utils/responsive'
import { cleanNumberAndHandleDDIBeforeSendToAPI } from '@/utils'
import { AppButton } from '@/components/AppButton'

export function RecoverPasswordWithPhoneForm() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const phoneInIdentifyForm = useAuthScopedStore((state) => state.phone)
  const setRecoverPasswordForm = useAuthScopedStore(
    (state) => state.setRecoverPasswordForm,
  )

  const setAuthPreferences = useAuthPreferencesStore(
    (state) => state.setAuthPreferences,
  )

  const { data: countriesData } = useGetCountriesCatalog()

  const getCountryIsoFromCode = (countryCode: string): string => {
    if (!countriesData?.countries) {
      return 'KW' // Default to Kuwait if API data not loaded yet
    }

    const code = countryCode.replace('+', '')
    const codeNumber = parseInt(code, 10)

    const foundCountry = countriesData.countries.find(
      (country) => country.code === codeNumber,
    )
    return foundCountry?.short_name || 'KW'
  }

  // Priority: phoneInIdentifyForm (from previous step) > preferences > null
  const [currentPhoneData, setCurrentPhoneData] = useState<PhoneData | null>(
    () => {
      if (phoneInIdentifyForm) return phoneInIdentifyForm

      const { countryCode, countryIsoCode, number } =
        useAuthPreferencesStore.getState()

      // If we have countryCode and number, create phone data
      if (countryCode && number) {
        return {
          countryCode,
          countryIsoCode: countryIsoCode || getCountryIsoFromCode(countryCode),
          number,
        }
      }

      return null
    },
  )

  const { phoneIsValid, setPhoneIsValid, errorMessage, setErrorMessage } =
    usePhoneValidation({
      phone: currentPhoneData,
      options: { autoValidate: true, debounceDelay: 600 },
    })

  useEffect(() => {
    const { countryCode, countryIsoCode, number } =
      useAuthPreferencesStore.getState()
    if (countryCode && number && !countryIsoCode && countriesData?.countries) {
      const derivedIsoCode = getCountryIsoFromCode(countryCode)
      if (derivedIsoCode !== 'KW' || countryCode === '+965') {
        // Only save if we found a match or it's actually Kuwait
        setAuthPreferences({ countryIsoCode: derivedIsoCode })
      }
    }
  }, [countriesData?.countries, setAuthPreferences])

  const [selectedMessagingMethod, setSelectedMessagingMethod] =
    useState<MessagingMethodCode | null>(null)
  const [selectedMethodData, setSelectedMethodData] =
    useState<MessagingMethodsDTO | null>(null)
  const [showMessagingMethodsSheet, setShowMessagingMethodsSheet] =
    useState(false)
  const [confirmedMessagingMethod, setConfirmedMessagingMethod] =
    useState(false)
  const [isRecoveryCodeSending, setIsRecoveryCodeSending] = useState(false)
  const phoneDataSchema = usePhoneDataSchema()

  const { handleSubmit, formState, setValue, setError, getValues } =
    useForm<IPhoneDataSchema>({
      resolver: zodResolver(phoneDataSchema),
      defaultValues: {
        phoneData: phoneInIdentifyForm ||
          currentPhoneData || {
            countryCode: '+965',
            countryIsoCode: 'KW',
            number: '',
          },
      },
      mode: 'onBlur',
    })

  const { mutateAsync: sendRecoveryCodeToPhone, isPending } =
    useSendRecoveryCodeToPhone({
      onSuccess: ({ user_id }) => {
        if (selectedMessagingMethod && selectedMethodData) {
          setRecoverPasswordForm({
            userId: user_id,
            recoveryMethod: 'phone',
            messagingMethod: {
              code: selectedMessagingMethod,
              imgUrl: selectedMethodData.img_url,
            },
          })
        }
        navigate('/auth/forgot-password/verification-code')
      },
      onError: ({ code, errorMessage }) => {
        if (!errorMessage) return

        if (code === ApiErrorCode.USER_NOT_FOUND) {
          setPhoneIsValid(false)
          setErrorMessage(errorMessage)
          setError(
            'phoneData.number',
            { message: errorMessage },
            { shouldFocus: true },
          )
          return
        }

        toast.error(errorMessage)
      },
    })

  const handleSubmitForm = async (formData: IPhoneDataSchema) => {
    // Save complete phone number to recover password form (API expects string)
    setRecoverPasswordForm({
      phone: `${formData.phoneData.countryCode}${formData.phoneData.number}`,
    })

    // If no messaging method is selected, show messaging methods sheet
    if (!selectedMessagingMethod || !confirmedMessagingMethod) {
      setShowMessagingMethodsSheet(true)
      return
    }
  }

  const handleMessagingMethodSelected = (
    method: MessagingMethodCode,
    methodData?: MessagingMethodsDTO,
  ) => {
    // Prevent multiple calls while already sending
    if (isRecoveryCodeSending || isPending) {
      return
    }

    setSelectedMessagingMethod(method)
    setSelectedMethodData(methodData || null)
    setConfirmedMessagingMethod(true)
    setShowMessagingMethodsSheet(false)
    handleContinueWithSelectedMethod(method)
  }

  const handleContinueWithSelectedMethod = async (
    method?: MessagingMethodCode,
  ) => {
    const currentPhoneData = getValues('phoneData')
    const methodToUse = method || selectedMessagingMethod

    // Prevent multiple calls
    if (isRecoveryCodeSending || isPending) {
      return
    }

    if (currentPhoneData && methodToUse) {
      setIsRecoveryCodeSending(true)

      try {
        const cleanPhoneNumber = cleanNumberAndHandleDDIBeforeSendToAPI(
          `${currentPhoneData.countryCode}${currentPhoneData.number}`,
        )

        await sendRecoveryCodeToPhone({
          phone_number: cleanPhoneNumber,
          send_method: methodToUse,
        })
      } finally {
        setIsRecoveryCodeSending(false)
      }
    }
  }

  useEffect(() => {
    if (phoneInIdentifyForm) {
      setValue('phoneData', phoneInIdentifyForm)
      setCurrentPhoneData(phoneInIdentifyForm)
      setRecoverPasswordForm({
        phone: `${phoneInIdentifyForm.countryCode}${phoneInIdentifyForm.number}`,
      })
    }
  }, [phoneInIdentifyForm, setValue, setRecoverPasswordForm])

  return (
    <>
      <BaseView gap={8}>
        <PhoneNumberInput
          value={getValues('phoneData')}
          onChange={(phoneData: PhoneData) => {
            setValue('phoneData', phoneData, {
              shouldValidate: phoneData.number.length > 5,
            })
            setCurrentPhoneData(phoneData)
            setRecoverPasswordForm({
              phone: `${phoneData.countryCode}${phoneData.number}`,
            })
            // Also save to preferences for future use
            setAuthPreferences({
              countryCode: phoneData.countryCode,
              countryIsoCode: phoneData.countryIsoCode,
              number: phoneData.number,
            })
          }}
          errorMessage={
            formState.errors.phoneData?.number?.message ||
            formState.errors.phoneData?.message ||
            errorMessage
          }
          withoutBorder
        />
      </BaseView>

      <BaseView mt="auto" mb={responsiveValueAdapter(12, true)}>
        <AppButton
          label={t('form:actions.confirm')}
          onPress={handleSubmit(handleSubmitForm)}
          disabled={!formState.isValid || !phoneIsValid}
          isLoading={isPending}
        />
      </BaseView>

      <MessagingMethodsBottomSheet
        type="recoverPassword"
        onMethodSelected={handleMessagingMethodSelected}
        isOpen={showMessagingMethodsSheet}
        onClose={() => setShowMessagingMethodsSheet(false)}
        isLoading={isPending}
        selectedMessagingMethod={selectedMessagingMethod}
        setSelectedMessagingMethod={setSelectedMessagingMethod}
      />
    </>
  )
}
